import React, { useState, useEffect, useMemo, useRef } from "react";
import BannerSection from "../../components/common/BannerSection";
import { Box, Button, useTheme, useMediaQuery } from "@mui/material";
import CampaignCard from "../../components/common/CampaignCard/CampaignCard";
import CampaignCardSkeleton from "../../components/common/CampaignCard/CampaignCardSkeleton";
import { getCampaigns } from "../../api/services/campaignService";
import CampaignSideBar from "./CampaignSideBar";
import { useDispatch, useSelector } from "react-redux";
import { fetchCmsHomeAction } from "../../store/slices/cmsSlice";
import { canViewPrivateCampaign } from "../../utils/authUtils";

const INITIAL_VISIBLE = 9;
const LOAD_MORE_BATCH = 9;

function CampaignList() {
  const dispatch = useDispatch();
  const cmsHome = useSelector((state) => state.cms.home);
  const { user, isAuthenticated } = useSelector((state) => state.auth);
  const [selectedCategory, setSelectedCategory] = useState("Urgent");
  const [campaigns, setCampaigns] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [visibleCount, setVisibleCount] = useState(INITIAL_VISIBLE);
  const loadMoreRef = useRef(null);

  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm")); // ≤600px

  // Fetch CMS data for banner image
  useEffect(() => {
    if (!cmsHome) {
      dispatch(fetchCmsHomeAction());
    }
  }, [cmsHome, dispatch]);

  // Extract banner image from CMS data
  const bannerImage = useMemo(() => {
    if (!cmsHome?.data || !Array.isArray(cmsHome.data)) return null;
    
    // Find section with page="Explore Campaign" and section="Banner" with status=1
    const bannerSection = cmsHome.data.find(
      (section) => 
        section?.page === "Explore Campaign" && 
        section?.section === "Banner" && 
        (section?.status === 1 || section?.status === true)
    );
    
    return bannerSection?.image || null;
  }, [cmsHome]);

  // Fetch campaigns from API
  useEffect(() => {
    const fetchCampaigns = async () => {
      try {
        setLoading(true);
        const response = await getCampaigns({ category: selectedCategory });
        if (response && response.data && Array.isArray(response.data)) {
          setCampaigns(response.data);
        } else {
          setCampaigns([]);
        }
        setError(null);
      } catch (err) {
        console.error("Error fetching campaigns:", err);
        setError(err);
        setCampaigns([]);
      } finally {
        setLoading(false);
      }
    };

    fetchCampaigns();
  }, [selectedCategory]);

  const filteredCampaigns = useMemo(() => {
    const privacyFiltered = campaigns.filter((c) => {
      if (c.IsPrivate === 0 || !c.IsPrivate) return true;
      if (c.IsPrivate === 1) return canViewPrivateCampaign(user, isAuthenticated);
      return true;
    });

    return privacyFiltered.sort((a, b) => {
      const dateA = new Date(a.createdAt || a.created_at || 0);
      const dateB = new Date(b.createdAt || b.created_at || 0);
      if (dateB.getTime() !== dateA.getTime()) return dateB.getTime() - dateA.getTime();
      return (b.id || 0) - (a.id || 0);
    });
  }, [campaigns, user, isAuthenticated]);

  // Reset visible count when category or list changes
  useEffect(() => {
    setVisibleCount(INITIAL_VISIBLE);
  }, [selectedCategory, filteredCampaigns.length]);

  useEffect(() => {
    if (loading || filteredCampaigns.length <= visibleCount) return;
    const el = loadMoreRef.current;
    if (!el) return;
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0]?.isIntersecting)
          setVisibleCount((prev) => Math.min(prev + LOAD_MORE_BATCH, filteredCampaigns.length));
      },
      { rootMargin: "100px", threshold: 0 }
    );
    observer.observe(el);
    return () => observer.disconnect();
  }, [loading, visibleCount, filteredCampaigns.length]);

  const campaignsToShow = useMemo(
    () => filteredCampaigns.slice(0, visibleCount),
    [filteredCampaigns, visibleCount]
  );
  const hasMore = filteredCampaigns.length > visibleCount;

  return (
    <>
      <Box sx={{ mb: isMobile ? "10px" : "40px", mt: isMobile ? "0" : "30px" }}>
        <BannerSection
          title="Explore Campaigns"
          subtitle="Your Action Can Make A Big Impact"
          backgroundImage={bannerImage}
        />
      </Box>

      <Box
        sx={{
          backgroundColor: "#E8E8E8",
          borderRadius: "36px",
          width: "100%",
          padding: "40px 0px 50px 0px",
        }}
      >
        <Box
          sx={{
            width: "94.44%",
            maxWidth: "1440px",
            display: "flex",
            flexWrap: "wrap", // ✅ allows wrap for sidebar + grid
            gap: "16px",
            margin: "0 auto",
          }}
        >
          {/* Sidebar */}
          <Box
            sx={{
              flexShrink: 0,
              width: isMobile ? "100%" : "156px", // full width on mobile, fixed on desktop
            }}
          >
            <CampaignSideBar
              selectedCategory={selectedCategory}
              setSelectedCategory={setSelectedCategory}
            />
          </Box>

          {/* Campaign Grid */}
          <Box
            sx={{
              display: "grid",
              rowGap: "20px",
              columnGap: "16px",
              flex: 1,
              gridTemplateColumns: {
                xs: "repeat(auto-fit, minmax(280px, 1fr))", // mobile
                sm: "repeat(auto-fit, minmax(310px, 1fr))", // ≥600px
                md: "repeat(auto-fit, minmax(320px, 1fr))", // ≥900px
              },
              justifyContent: "flex-start", // Align cards to left to prevent large gaps
              height: "100%",
              // Limit grid width only when there are exactly 2 cards to prevent large gaps
              ...(campaignsToShow.length === 2 && {
                maxWidth: {
                  xs: "auto",
                  sm: "auto",
                  md: `${(2 * 417) + 16}px`, // 2 cards + 1 gap
                },
              }),
            }}
          >
            {loading ? (
              <>
                {[...Array(9)].map((_, index) => (
                  <Box key={`skeleton-${index}`}>
                    <CampaignCardSkeleton isCampaign={true} />
                  </Box>
                ))}
              </>
            ) : error ? (
              <Box sx={{ gridColumn: "1 / -1", background: "#fff", borderRadius: "16px", p: "20px" }}>
                <Box sx={{ fontWeight: 700, color: "#1D1D1B", mb: "6px" }}>Unable to load campaigns</Box>
                <Box sx={{ color: "#686866", fontSize: "14px" }}>
                  {error?.message || "An unexpected error occurred. Please try again."}
                </Box>
                <Button
                  variant="contained"
                  onClick={() => window.location.reload()}
                  sx={{ mt: "14px", background: "#F04951", "&:hover": { background: "#F04951" } }}
                >
                  Retry
                </Button>
              </Box>
            ) : filteredCampaigns.length === 0 ? (
              <Box
                sx={{
                  gridColumn: "1 / -1",
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "center",
                  justifyContent: "center",
                  padding: "60px 20px",
                  background: "#fff",
                  borderRadius: "16px",
                  textAlign: "center",
                }}
              >
                <Box sx={{ fontSize: "48px", mb: "16px" }}>🔍</Box>
                <Box sx={{ fontWeight: 700, fontSize: "18px", color: "#1D1D1B", mb: "8px" }}>
                  No campaigns found
                </Box>
                <Box sx={{ color: "#686866", fontSize: "14px", maxWidth: "320px" }}>
                  There are no campaigns in this category right now. Try selecting a different category.
                </Box>
              </Box>
            ) : (
              <>
                {campaignsToShow.map((campaign) => (
                  <Box
                    key={campaign.id}
                    sx={
                      campaignsToShow.length === 1
                        ? {
                            maxWidth: { xs: "auto", sm: "auto", md: "387px" },
                            justifySelf: "flex-start",
                          }
                        : {}
                    }
                  >
                    <CampaignCard campaignData={campaign} isCampaign={true} customMobileImageHeight="212px" />
                  </Box>
                ))}
                {hasMore && (
                  <Box
                    ref={loadMoreRef}
                    sx={{
                      gridColumn: "1 / -1",
                      height: "20px",
                      minHeight: "20px",
                    }}
                    aria-hidden="true"
                  />
                )}
              </>
            )}
          </Box>
        </Box>
      </Box>
    </>
  );
}

export default CampaignList;
